import { Contact, ContactUpsertInput } from '@api/generated/graphql';
import { Drawer } from '@components/Drawer';
import FormFooterButtons from '@components/Drawer/components/FormFooterButtons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@servicetitan/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import sharedStrings from 'src/constants/strings';
import strings from '../strings';
import { transformContacts } from '../transformContacts';
import EditContactDrawerPageHeader from './components/EditContactDrawerPageHeader';
import EditContactsDrawerPage from './components/EditContactsDrawerPage';
import { ViewAllContactsDrawerPage } from './components/ViewAllContactsDrawerPage';
import DrawerHeader from '../../Drawer/DrawerHeader/DrawerHeader';
import ToastModule from '@modules/ToastModule';
import editContactsValidationSchema, {
  EditContactsValidationType,
} from '../validations';
import { useUpsertCustomerContactsMutation } from '../api/useUpsertCustomerContactsMutation';
import { useContactSupportButton } from '@lib/hooks/useContactSupportButton';

type ViewAllContactsDrawerProps = {
  open: boolean;
  customerName: string;
  contacts: Contact[];
  customerId: string;
  tenantId: string;
  customerContactsLoading: boolean;
  customerContactsError: boolean;
  onClose: () => void;
  refetchCustomerContacts: () => void;
};

enum DrawerPageState {
  View,
  Edit,
}

const ViewAllContactsDrawer = ({
  customerId,
  tenantId,
  customerName,
  contacts,
  customerContactsLoading: contactsLoading,
  customerContactsError: contactsError,
  open,
  onClose,
  refetchCustomerContacts,
}: ViewAllContactsDrawerProps) => {
  const customerContactsCount = contacts.length;
  const [drawerPageState, setDrawerPageState] = useState(DrawerPageState.View);
  const [exitPromptOpened, setExitPromptOpened] = useState(false);

  const {
    upsertCustomerContactsMutation,
    loading: submitLoading,
    reset: resetUpsertMutation,
  } = useUpsertCustomerContactsMutation();
  const contactSupportProps = useContactSupportButton();

  // stores the callback to be executed when the exit prompt is confirmed
  const [exitPromptCallback, setExitPromptCallback] = useState<() => void>();

  const isEditPage = drawerPageState === DrawerPageState.Edit;

  // Applies the phone mask to non-email fields
  const originalContactsMasked = useMemo(
    () => transformContacts(contacts),
    [contacts],
  );

  const defaultFormValues = useMemo(
    () => ({
      editContacts: { contacts: originalContactsMasked },
    }),
    [originalContactsMasked],
  );

  const formMethods = useForm<EditContactsValidationType>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(editContactsValidationSchema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, defaultValues },
  } = formMethods;

  const handleSubmitFailure = () => {
    ToastModule.showToast({
      status: 'critical',
      title: strings.errorToast.title,
      toastBody: strings.errorToast.message,
      primaryActionName: 'Try Again',
      onPrimaryActionClick: handleSubmit(onSubmit),
      ...contactSupportProps,
    });
  };

  const onEditSubmitSuccess = () => {
    refetchCustomerContacts();
    setDrawerPageState(DrawerPageState.View);
    ToastModule.showToast({
      status: 'success',
      title: strings.successToast.title,
    });
    resetUpsertMutation();
  };

  const handleNavBackToView = () => {
    handleIsDirtyAction(() => setDrawerPageState(DrawerPageState.View));
  };

  const handleOnClose = () => {
    const closeAction = () => {
      onClose();
      setDrawerPageState(DrawerPageState.View);
    };
    handleIsDirtyAction(closeAction);
  };

  const handleIsDirtyAction = (action: () => void) => {
    if (isDirty) {
      setExitPromptOpened(true);
      setExitPromptCallback(() => action);
    } else {
      action();
    }
  };

  const handleDiscardChanges = () => {
    setExitPromptOpened(false);
    reset(defaultValues);
    exitPromptCallback?.();
  };

  const onSubmit = async (data: EditContactsValidationType) => {
    if (!isDirty) {
      handleNavBackToView();
      return;
    }

    const contactsInput: ContactUpsertInput[] = data.editContacts.contacts.map(
      (contact) => ({
        ...contact,
        id: contact.id ? contact.id.toString() : undefined,
      }),
    );

    const result = await upsertCustomerContactsMutation({
      input: contactsInput,
      tenantId,
      customerId,
    });

    if (result.data?.upsertCustomerContact) {
      onEditSubmitSuccess();
    } else {
      handleSubmitFailure();
    }
  };

  useEffect(() => {
    reset(defaultFormValues);
  }, [reset, defaultFormValues]);

  return (
    <>
      <Drawer
        open={open}
        onClose={() => handleOnClose()}
        header={
          drawerPageState === DrawerPageState.View ? (
            <DrawerHeader
              title={`${customerName}'s Contacts`}
              count={customerContactsCount}
            />
          ) : (
            <EditContactDrawerPageHeader
              customerName={customerName}
              onBackClick={() => handleNavBackToView()}
            />
          )
        }
        footer={
          isEditPage ? (
            <FormFooterButtons
              loading={submitLoading}
              primaryText={sharedStrings.labels.editContacts.primaryAction}
              secondaryText={sharedStrings.labels.editContacts.secondaryAction}
              onSecondaryClick={() => handleNavBackToView()}
              onPrimaryClick={handleSubmit(onSubmit)}
            />
          ) : undefined
        }
      >
        {drawerPageState === DrawerPageState.View && (
          <ViewAllContactsDrawerPage
            contacts={contacts}
            loading={contactsLoading}
            error={contactsError}
            onEditClick={() => setDrawerPageState(DrawerPageState.Edit)}
          />
        )}
        {drawerPageState === DrawerPageState.Edit && (
          <EditContactsDrawerPage
            formMethods={formMethods}
            onSubmit={handleSubmit(onSubmit)}
          />
        )}
      </Drawer>
      <Dialog
        open={exitPromptOpened}
        focusTrapOptions={{ disabled: true }}
        title={sharedStrings.labels.dialog.exitWarning.title}
        portal={true}
        onPrimaryActionClick={handleDiscardChanges}
        primaryActionName={
          sharedStrings.labels.dialog.exitWarning.primaryAction
        }
        onSecondaryActionClick={() => setExitPromptOpened(false)}
        secondaryActionName={
          sharedStrings.labels.dialog.exitWarning.secondaryAction
        }
        negative
      >
        {sharedStrings.labels.dialog.exitWarning.body}
      </Dialog>
    </>
  );
};

export default ViewAllContactsDrawer;
